import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FlaskConical, Save, Search, Package, CheckCircle, XCircle, Loader2, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { DatePicker } from '../components/ui/date-picker';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import api from '../services/api';
import { toast } from 'sonner';
import { format } from 'date-fns';

export const NewSensorikaPage = ({ sensoryType = 'hotovky' }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fields, setFields] = useState([]);
  const [formData, setFormData] = useState({
    test_date: format(new Date(), 'dd.MM.yyyy'),
    food_name: '',
    batch: '',
    program: '',
    production_date: '',
    dmt: '',
    produced_quantity: '',
    custom_fields: {},
    sensory_type: sensoryType,
    production_record_id: null
  });

  // Batch search state
  const [batchSearchQuery, setBatchSearchQuery] = useState('');
  const [batchSearchResults, setBatchSearchResults] = useState([]);
  const [balirnaResults, setBalirnaResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showBatchSearch, setShowBatchSearch] = useState(true);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [showManualEntry, setShowManualEntry] = useState(false);

  const typeLabel = sensoryType === 'hotovky' ? 'Hotovky' : 'Krabičky';
  const basePath = `/senzorika/${sensoryType}`;
  const isHotovky = sensoryType === 'hotovky';

  useEffect(() => {
    setFormData(prev => ({ ...prev, sensory_type: sensoryType }));
  }, [sensoryType]);

  useEffect(() => {
    fetchFields();
  }, [sensoryType]);

  const fetchFields = async () => {
    try {
      const response = await api.get('/sensory/test-fields', { params: { sensory_type: sensoryType } });
      setFields(response.data.filter(f => f.is_active));
      
      const defaultValues = {};
      response.data.forEach(field => {
        if (field.default_today && field.field_type === 'date') {
          defaultValues[field.name] = format(new Date(), 'dd.MM.yyyy');
        }
      });
      if (Object.keys(defaultValues).length > 0) {
        setFormData(prev => ({ ...prev, custom_fields: { ...prev.custom_fields, ...defaultValues } }));
      }
    } catch (error) {
      toast.error('Chyba při načítání polí');
    }
  };

  // Debounced batch search
  const searchBatch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setBatchSearchResults([]);
      setBalirnaResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      // For Hotovky, filter by DRUH = PS
      const productionParams = { batch: query };
      if (isHotovky) {
        productionParams.druh = 'PS';
      }
      
      const [productionRes, balirnaRes] = await Promise.all([
        api.get('/production/search-batch', { params: productionParams }),
        api.get('/balirna/search-batch', { params: { batch: query } })
      ]);
      
      setBatchSearchResults(productionRes.data.results || []);
      setBalirnaResults(balirnaRes.data.results || []);
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Chyba při vyhledávání');
    } finally {
      setSearchLoading(false);
    }
  }, [isHotovky]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (batchSearchQuery) {
        searchBatch(batchSearchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [batchSearchQuery, searchBatch]);

  const selectBatchFromProduction = (record) => {
    setSelectedBatch(record);
    setShowBatchSearch(false);
    
    // Auto-fill form data from production record
    const productionDate = record.production_date 
      ? format(new Date(record.production_date), 'dd.MM.yyyy')
      : '';
    
    // Format DMT - remove time part if present
    let dmtFormatted = '';
    if (record.dmt) {
      try {
        dmtFormatted = format(new Date(record.dmt), 'dd.MM.yyyy');
      } catch {
        // If parsing fails, try to extract just the date part
        dmtFormatted = record.dmt.split('T')[0];
      }
    }
    
    setFormData(prev => ({
      ...prev,
      batch: record.batch || record.doklad || '',
      food_name: record.product_name || '',
      production_date: productionDate,
      dmt: dmtFormatted,
      produced_quantity: record.quantity || '',
      production_record_id: record.id
    }));
    
    toast.success('Data načtena z výroby');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    for (const field of fields) {
      if (field.required) {
        const value = getFieldValue(field.name);
        if (!value) {
          toast.error(`Pole "${field.name}" je povinné`);
          return;
        }
      }
    }

    setLoading(true);
    try {
      const response = await api.post('/sensory/tests', formData);
      toast.success('Zkouška vytvořena');
      navigate(`${basePath}/${response.data.id}`);
    } catch (error) {
      toast.error('Chyba při vytváření zkoušky');
    } finally {
      setLoading(false);
    }
  };

  const getFieldValue = (fieldName) => {
    const mapping = {
      'Den zkoušky': formData.test_date,
      'Název jídla': formData.food_name,
      'Šarže': formData.batch,
      'Program': formData.program,
      'Datum výroby': formData.production_date,
      'DMT (datum spotřeby)': formData.dmt,
      'Vyrobené množství': formData.produced_quantity
    };
    return mapping[fieldName] ?? formData.custom_fields[fieldName] ?? '';
  };

  const setFieldValue = (fieldName, value) => {
    const mapping = {
      'Den zkoušky': 'test_date',
      'Název jídla': 'food_name',
      'Šarže': 'batch',
      'Program': 'program',
      'Datum výroby': 'production_date',
      'DMT (datum spotřeby)': 'dmt',
      'Vyrobené množství': 'produced_quantity'
    };
    
    const standardField = mapping[fieldName];
    if (standardField) {
      setFormData(prev => ({ ...prev, [standardField]: value }));
    } else {
      setFormData(prev => ({
        ...prev,
        custom_fields: { ...prev.custom_fields, [fieldName]: value }
      }));
    }
  };

  const renderField = (field) => {
    const value = getFieldValue(field.name);
    
    if (field.field_type === 'date') {
      return (
        <DatePicker
          value={value}
          onChange={(dateStr) => setFieldValue(field.name, dateStr || '')}
        />
      );
    }
    
    if (field.field_type === 'number') {
      return (
        <Input
          type="number"
          value={value}
          onChange={(e) => setFieldValue(field.name, e.target.value)}
          placeholder={`Zadejte ${field.name.toLowerCase()}`}
        />
      );
    }
    
    return (
      <Input
        type="text"
        value={value}
        onChange={(e) => setFieldValue(field.name, e.target.value)}
        placeholder={`Zadejte ${field.name.toLowerCase()}`}
      />
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd.MM.yyyy');
    } catch {
      return dateStr;
    }
  };

  // For Hotovky - show batch search first
  if (isHotovky && showBatchSearch && !showManualEntry) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(basePath)}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Nová senzorická zkouška - {typeLabel}</h1>
              <p className="text-slate-500">Vyhledejte šarži nebo zadejte údaje ručně</p>
            </div>
          </div>

          {/* Batch Search Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="w-5 h-5 text-purple-600" />
                Vyhledání šarže
              </CardTitle>
              <CardDescription>
                Zadejte číslo šarže nebo dokladu pro načtení údajů z výroby
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Zadejte šarži, číslo dokladu..."
                  value={batchSearchQuery}
                  onChange={(e) => setBatchSearchQuery(e.target.value)}
                  className="pl-10"
                  autoFocus
                />
                {searchLoading && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-purple-600" />
                )}
              </div>

              {/* Production Results */}
              {batchSearchResults.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Výsledky z Výroby ({batchSearchResults.length})
                  </h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {batchSearchResults.map((record) => (
                      <div
                        key={record.id}
                        className="p-3 border rounded-lg hover:border-purple-300 hover:bg-purple-50 cursor-pointer transition-colors"
                        onClick={() => selectBatchFromProduction(record)}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-slate-900">{record.product_name || 'Bez názvu'}</p>
                            <p className="text-sm text-slate-500">
                              Šarže: <span className="font-mono">{record.batch || record.doklad}</span>
                            </p>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            record.status === 'Dokončený' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {record.status || 'N/A'}
                          </span>
                        </div>
                        <div className="mt-2 flex gap-4 text-xs text-slate-500">
                          <span>Datum: {formatDate(record.production_date)}</span>
                          {record.dmt && <span>DMT: {formatDate(record.dmt)}</span>}
                          {record.quantity && <span>Množství: {record.quantity}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Balirna Results */}
              {balirnaResults.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Výsledky z Balírny ({balirnaResults.length})
                  </h3>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {balirnaResults.map((record) => (
                      <div
                        key={record.id}
                        className="p-3 border rounded-lg bg-orange-50 border-orange-200"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-slate-900">{record.product_name}</p>
                            <p className="text-sm text-slate-500">
                              Šarže: <span className="font-mono">{record.batch_number}</span>
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {record.packaging_correct ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-600" />
                            )}
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              record.status === 'completed' 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {record.status === 'completed' ? 'Dokončeno' : 'Rozpracováno'}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          Datum: {formatDate(record.date)} | Zapsal: {record.created_by_name}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No results */}
              {batchSearchQuery.length >= 2 && !searchLoading && 
               batchSearchResults.length === 0 && balirnaResults.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <Package className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>Žádné výsledky pro &quot;{batchSearchQuery}&quot;</p>
                </div>
              )}

              {/* Manual entry button */}
              <div className="pt-4 border-t flex justify-between items-center">
                <p className="text-sm text-slate-500">
                  Nenašli jste šarži? Můžete zadat údaje ručně.
                </p>
                <Button 
                  variant="outline"
                  onClick={() => setShowManualEntry(true)}
                >
                  Zadat ručně
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  // Regular form (for Krabicky or after batch selection / manual entry)
  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => {
              if (isHotovky && !showManualEntry && selectedBatch) {
                setSelectedBatch(null);
                setShowBatchSearch(true);
              } else if (isHotovky && showManualEntry) {
                setShowManualEntry(false);
                setShowBatchSearch(true);
              } else {
                navigate(basePath);
              }
            }}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Nová senzorická zkouška - {typeLabel}</h1>
            {selectedBatch ? (
              <p className="text-slate-500">
                Šarže: <span className="font-mono font-medium">{selectedBatch.batch || selectedBatch.doklad}</span>
                {' - '}{selectedBatch.product_name}
              </p>
            ) : (
              <p className="text-slate-500">
                {showManualEntry ? 'Ruční zadání údajů' : 'Vyplňte údaje o zkoušce'}
              </p>
            )}
          </div>
        </div>

        {/* Selected batch info */}
        {selectedBatch && (
          <Card className="border-purple-200 bg-purple-50">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Package className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{selectedBatch.product_name}</p>
                    <p className="text-sm text-slate-500">
                      Šarže: {selectedBatch.batch || selectedBatch.doklad} | 
                      Datum výroby: {formatDate(selectedBatch.production_date)}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedBatch(null);
                    setShowBatchSearch(true);
                    setBatchSearchQuery('');
                  }}
                >
                  Změnit
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-6">
          <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
              <FlaskConical className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">Údaje o zkoušce</h2>
              <p className="text-sm text-slate-500">Základní informace o senzorické zkoušce</p>
            </div>
          </div>

          <div className="grid gap-5">
            {fields.map((field) => (
              <div key={field.id} className="space-y-2">
                <Label className="flex items-center gap-1">
                  {field.name}
                  {field.required && <span className="text-red-500">*</span>}
                </Label>
                {renderField(field)}
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <Button 
              type="button" 
              variant="outline"
              onClick={() => navigate(basePath)}
            >
              Zrušit
            </Button>
            <Button 
              type="submit"
              disabled={loading}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
            >
              <Save className="h-4 w-4 mr-2" />
              {loading ? 'Ukládám...' : 'Vytvořit zkoušku'}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default NewSensorikaPage;
